import { ReportMetadata, SubstitutionConfig } from '../types';
import {
    CalendarProject,
    SubstituteAssignment,
    generateSubstituteIcs,
    sanitizeFileName,
    substituteDueDates,
} from './calendarService';
import { resolveIntervalConfig } from './dateService';
import { toBase64Utf8 } from './emlBuilder';
import { sendReportEmail } from './cloudService';
import {
    FARBE_LEER,
    FARBE_WARNUNG,
    TD,
    esc,
    formatDate,
    formatWeekday,
    renderKastenHinweis,
    renderMailRahmen,
    renderOutlookKasten,
    renderProjektZelle,
    renderTabelle,
} from './mailLayout';

/**
 * Urlaubsvertretung per E-Mail: Der Vertreter bekommt EINE Mail mit allen
 * übernommenen Projekten, dem jeweiligen Zeitraum, den konkreten
 * Begehungsterminen und einer ICS-Datei für Outlook.
 *
 * Versendet wird über denselben Weg wie jeder Bericht (`/api/send-email`,
 * Absender `report@gefas-mbh.de`); Antworten landen per Reply-To beim
 * Koordinator, der die Vertretung geplant hat.
 */

export interface SubstitutionMailPlan {
    substituteName: string;
    substituteEmail: string;
    assignments: SubstituteAssignment[];
}

export interface SubstitutionMailResult {
    substituteEmail: string;
    substituteName: string;
    projectCount: number;
    appointmentCount: number;
    ok: boolean;
    error?: string;
}

const readMeta = (project: CalendarProject | null | undefined): ReportMetadata | null =>
    (project?.data?.metadata as ReportMetadata) || null;

/**
 * Bündelt Zuweisungen je Vertreter (Schlüssel: E-Mail, kleingeschrieben).
 * Ein Vertreter bekommt genau eine Mail, egal über wie viele Projekte und
 * Einzelwochen die Vertretung verteilt wurde.
 */
export const groupBySubstitute = (assignments: SubstituteAssignment[]): SubstitutionMailPlan[] => {
    const map = new Map<string, SubstitutionMailPlan>();
    assignments.forEach(a => {
        const email = (a.entry.substituteEmail || '').trim().toLowerCase();
        if (!email) return;
        const plan = map.get(email) || {
            substituteEmail: email,
            substituteName: a.entry.substituteName || email,
            assignments: [],
        };
        plan.assignments.push(a);
        map.set(email, plan);
    });
    return Array.from(map.values());
};

/** Gesamtzeitraum über alle Einträge eines Vertreters. */
export const planPeriod = (plan: SubstitutionMailPlan): { start: string; end: string } => {
    const starts = plan.assignments.map(a => a.entry.startDate).filter(Boolean).sort();
    const ends = plan.assignments.map(a => a.entry.endDate).filter(Boolean).sort();
    return { start: starts[0] || '', end: ends[ends.length - 1] || '' };
};

/**
 * Anzahl verschiedener Projekte. Die Matrix kann dasselbe Projekt in mehreren
 * Wochen demselben Vertreter zuweisen — das sind mehrere Zeilen, aber EIN
 * übernommenes Projekt.
 */
export const countProjects = (plan: SubstitutionMailPlan): number =>
    new Set(plan.assignments.map(a => a.project?.id).filter(Boolean)).size;

export const buildSubject = (plan: SubstitutionMailPlan): string => {
    const { start, end } = planPeriod(plan);
    const count = countProjects(plan);
    return `Urlaubsvertretung: ${count} Projekt${count === 1 ? '' : 'e'} vom ${formatDate(start)} bis ${formatDate(end)}`;
};

interface PlanRow {
    projectNumber: string;
    projectName: string;
    projectAddress: string;
    coordinator: string;
    startDate: string;
    endDate: string;
    dates: string[];
    manual: boolean;
}

/** Eine Zeile je Zuweisung, chronologisch nach Vertretungsbeginn. */
export const buildRows = (plan: SubstitutionMailPlan): PlanRow[] =>
    plan.assignments
        .map(({ project, entry }): PlanRow | null => {
            const meta = readMeta(project);
            if (!meta) return null;
            // Gleiche Turnus-Auflösung wie Urlaubsplaner und ICS: fehlende
            // Konfiguration = wöchentlich, nur echtes `manual` (oder SiFa) ist manuell.
            const manual = resolveIntervalConfig(meta).type === 'manual';
            return {
                projectNumber: project.project_number || meta.projectNumber || '',
                projectName: meta.projectName || project.name || 'Projekt',
                projectAddress: meta.projectAddress || '',
                coordinator: meta.coordinator || '',
                startDate: entry.startDate,
                endDate: entry.endDate,
                dates: manual ? [] : substituteDueDates(project, entry),
                manual,
            };
        })
        .filter((r): r is PlanRow => r !== null)
        .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.projectNumber.localeCompare(b.projectNumber));

const renderDates = (row: PlanRow): string => {
    if (row.manual) return `<span style="color:${FARBE_WARNUNG};">Turnus manuell &ndash; Termine nach Absprache</span>`;
    if (row.dates.length === 0) return `<span style="color:${FARBE_LEER};">kein Termin im Zeitraum</span>`;
    return row.dates
        .map(d => `<span style="white-space:nowrap;">${formatWeekday(d)}. ${formatDate(d)}</span>`)
        .join('<br>');
};

/**
 * Rahmen, Tabelle und Outlook-Kasten kommen aus `mailLayout.ts`, damit die
 * Kickoff-Mail genauso aussieht. Die Ausgabe ist dieselbe wie vor dem Umbau.
 */
export const buildHtml = (plan: SubstitutionMailPlan, plannerName?: string): string => {
    const rows = buildRows(plan);
    const { start, end } = planPeriod(plan);
    const appointmentCount = rows.reduce((sum, r) => sum + r.dates.length, 0);
    const projectCount = countProjects(plan);

    const tableRows = rows.map(r => `
        <tr>
            <td style="${TD}">
                ${renderProjektZelle(r.projectName, r.projectNumber, r.projectAddress)}
            </td>
            <td style="${TD}white-space:nowrap;">${formatDate(r.startDate)}<br>bis ${formatDate(r.endDate)}</td>
            <td style="${TD}font-size:13px;">${renderDates(r)}</td>
            <td style="${TD}font-size:13px;color:#475569;">${esc(r.coordinator || '-')}</td>
        </tr>`).join('');

    const anrede = `<p style="margin:0 0 16px;">Hallo ${esc(plan.substituteName)},</p>
    <p style="margin:0 0 20px;">
        Sie übernehmen im oben genannten Zeitraum <strong>${projectCount} Projekt${projectCount === 1 ? '' : 'e'}</strong>
        mit insgesamt <strong>${appointmentCount} Begehungstermin${appointmentCount === 1 ? '' : 'en'}</strong>.
        ${plannerName ? `Geplant von ${esc(plannerName)}.` : ''}
    </p>`;

    return renderMailRahmen({
        titel: 'Urlaubsvertretung',
        unterzeile: `${formatDate(start)} bis ${formatDate(end)}`,
        bloecke: [
            anrede,
            renderTabelle(['Projekt', 'Zeitraum', 'Begehungstermine', 'Stamm-Koordinator'], tableRows),
            renderOutlookKasten({
                zusatz: renderKastenHinweis([
                    'Die Termine tragen das Kürzel <strong>[V]</strong> im Betreff und enthalten Adresse, Bauherr und Beteiligte in der Beschreibung.',
                    'Sie stehen als Einzeltermine im Kalender und enden automatisch mit dem Vertretungszeitraum.',
                ]),
            }),
        ],
        antwortSatz: 'Antworten gehen an den Absender der Vertretungsplanung.',
    });
};

export const buildIcsFilename = (plan: SubstitutionMailPlan): string => {
    const { start, end } = planPeriod(plan);
    return `Vertretung_${sanitizeFileName(plan.substituteName)}_${start}_${end}.ics`;
};

/**
 * Verschickt eine Vertretungsmail je Vertreter. Ein Fehlschlag bricht die
 * übrigen Mails NICHT ab — das Speichern der Vertretung ist bereits erfolgt,
 * der Versand ist Zusatzinformation und darf nicht alles mitreißen.
 */
export const sendSubstitutionPlans = async (
    plans: SubstitutionMailPlan[],
    plannerName?: string,
): Promise<SubstitutionMailResult[]> => {
    const results: SubstitutionMailResult[] = [];

    for (const plan of plans) {
        const rows = buildRows(plan);
        const appointmentCount = rows.reduce((sum, r) => sum + r.dates.length, 0);
        const base: SubstitutionMailResult = {
            substituteEmail: plan.substituteEmail,
            substituteName: plan.substituteName,
            projectCount: countProjects(plan),
            appointmentCount,
            ok: false,
        };

        if (!plan.substituteEmail) {
            results.push({ ...base, error: 'Keine E-Mail-Adresse hinterlegt.' });
            continue;
        }

        try {
            const ics = generateSubstituteIcs(plan.assignments);
            await sendReportEmail({
                recipients: [plan.substituteEmail],
                subject: buildSubject(plan),
                html: buildHtml(plan, plannerName),
                filename: buildIcsFilename(plan),
                icsBase64: toBase64Utf8(ics),
                icsFilename: buildIcsFilename(plan),
            });
            results.push({ ...base, ok: true });
        } catch (e: any) {
            results.push({ ...base, error: e?.message || 'Unbekannter Fehler beim Versand.' });
        }
    }

    return results;
};

/** Zuweisungen aus einem frisch gespeicherten Eintrag je Projekt. */
export const assignmentsFor = (projects: ReadonlyArray<CalendarProject>, entry: SubstitutionConfig): SubstituteAssignment[] =>
    projects.map(project => ({ project, entry }));
